import React from "react";
import { VotingViewQuestion as VotingViewQuestionType, convertHtmlToText, htmlToPlainText } from "@lubbezposrednio-frontend/core";
import { Box, Flex, Heading, Icon, Text, VStack } from "@chakra-ui/react";
import { FiCheck, FiMinusCircle } from "react-icons/fi";
import { VotingViewModel } from "../../VotingViewModel";
import { observer } from "mobx-react-lite";

type VotingViewQuestionProps = {
  question: VotingViewQuestionType;
  model: VotingViewModel;
  index?: number;
};

const Option: React.FC<{
  label: React.ReactNode;
  selected: boolean;
  multiple?: boolean;
  muted?: boolean;
  onClick: () => void;
}> = ({ label, selected, multiple, muted, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap="14px"
    textAlign="left"
    p={{ base: "13px 14px", md: "14px 18px" }}
    borderRadius="14px"
    border="2px solid"
    borderColor={selected ? "accent" : "rgba(67,0,152,0.12)"}
    bg={selected ? "rgba(229,31,68,0.05)" : "white"}
    transition="all 0.15s"
    _hover={{ borderColor: selected ? "accent" : "rgba(67,0,152,0.3)" }}
  >
    <Flex
      w="22px"
      h="22px"
      borderRadius={multiple ? "7px" : "full"}
      border="2px solid"
      borderColor={selected ? "accent" : "gray.300"}
      bg={selected ? "accent" : "white"}
      align="center"
      justify="center"
      flexShrink={0}
    >
      {selected && (multiple ? <Icon as={FiCheck} color="white" boxSize="13px" /> : <Box w="9px" h="9px" borderRadius="full" bg="white" />)}
    </Flex>
    <Text fontSize={{ base: "15px", md: "16px" }} fontWeight={selected ? "700" : "500"} color={muted ? "gray.500" : "primary"}>
      {label}
    </Text>
  </Flex>
);

export const VotingViewQuestion: React.FC<VotingViewQuestionProps> = observer(({ question, model, index }) => {
  const error = model.getQuestionError(question.id);
  const isMultiple = question.qtype === "multiple";
  const selectedIds = question.selectedAnswerIds ?? [];
  const abstained = !!question.abstained;

  return (
    <Box
      w="100%"
      bg="white"
      borderRadius="20px"
      border="1px solid"
      borderColor={error ? "rgba(229,31,68,0.35)" : "rgba(67,0,152,0.08)"}
      boxShadow="0 6px 20px rgba(67,0,152,0.05)"
      p={{ base: "22px", md: "28px" }}
    >
      <Flex align="flex-start" gap="12px" mb="6px">
        {index != null && (
          <Flex w="28px" h="28px" borderRadius="full" bg="primary" color="white" align="center" justify="center" fontWeight="800" fontSize="13px" flexShrink={0} mt="2px">
            {index + 1}
          </Flex>
        )}
        <Heading fontSize={{ base: "18px", md: "20px" }} fontWeight="800" color="primary" lineHeight="1.35">
          {convertHtmlToText(question.content)}
        </Heading>
      </Flex>

      <Text fontSize="13px" color="gray.400" mb="16px" pl={index != null ? "40px" : 0}>
        {isMultiple ? `Wybierz ${question.max_selections ? `do ${question.max_selections}` : "dowolną liczbę"} odpowiedzi.` : "Wybierz jedną odpowiedź."}
      </Text>

      <VStack align="stretch" spacing="10px">
        {question.answers.map(answer => (
          <Option
            key={answer.id}
            label={htmlToPlainText(answer.content)}
            selected={selectedIds.includes(answer.id)}
            multiple={isMultiple}
            onClick={() => model.selectAnswer(question.id, answer.id)}
          />
        ))}

        {question.allow_abstain && (
          <Option
            label={
              <Flex align="center" gap="7px">
                <Icon as={FiMinusCircle} boxSize="15px" />
                Wstrzymuję się
              </Flex>
            }
            selected={abstained}
            muted
            onClick={() => model.setAbstain(question.id)}
          />
        )}
      </VStack>

      {error && (
        <Text color="accent" fontSize="14px" fontWeight="600" mt="14px">
          {error}
        </Text>
      )}
    </Box>
  );
});
